import {
  useCreateShareMutation,
  useGetCurrentUserQuery,
  useGetOrganizationQuery,
  useGetTourQuery,
} from '@carboncopyco/data';
import {
  type IntegrationVendor,
  type ShareLink,
  ShareSecurityLevel,
  ShareSourceType,
  type Tour,
} from '@carboncopyco/types';
import { TextBase, notify } from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  removeUndefinedValues,
  useAnalytics,
} from '@carboncopyco/utils';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo, useState } from 'react';
import { CopyShareLinkModal } from '../CopyShareLinkModal';
import type { ShareTourModalState } from '../ShareTourModal';
import { ShareTourModal } from '../ShareTourModal';

const location = 'tour_info_page_create_share_modal';

type CreateShareTourFlowProps = {
  organizationId: string;
  workspaceId: string;
  tourId: Tour['id'];
  goToShares: () => void;
  toursShareAspectRatioEnabled: boolean;
  onShareSuccess?: (share: ShareLink) => void;
};

export const CreateShareTourFlow = NiceModal.create(
  ({
    organizationId,
    workspaceId,
    tourId,
    goToShares,
    toursShareAspectRatioEnabled,
    onShareSuccess,
  }: CreateShareTourFlowProps) => {
    const modal = useModal();
    const flags = useFlags();
    const { trackEvent } = useAnalytics();
    const { data: tour } = useGetTourQuery({
      workspaceId,
      organizationId,
      id: tourId,
    });
    const { data: userResponse } = useGetCurrentUserQuery();
    const { user } = userResponse || {};
    const { data: organization } = useGetOrganizationQuery(
      {
        id: organizationId,
      },
      { skip: !organizationId }
    );
    const initialShareTourModalValues: ShareTourModalState = useMemo(
      () => ({
        linkDescription: `Link for ${tour?.tourName}`,
        skipWelcome: false,
        security: {
          level: ShareSecurityLevel.internal,
          emailDomain: user?.email.split('@').slice(-1)[0],
        },
        isActive: false,
        expiresAt: null,
        daysToExpire: null,
        autoSyncUpdates: true,
        toggleExpiration: false,
        tourAspectRatio: flags?.tourCreateShareDefaultRatioFixed
          ? 'fixedWidthAndHeight'
          : 'responsive',
        cover: { requireName: false, requireEmail: true },
        salesforceLeads: false,
        hubspotLeads: false,
        slackIntegration: false,
        welcomeMatTitle: flags.welcomeMatTitle
          ? undefined
          : `${organization.organizationName} product tour`,
      }),
      [
        flags?.tourCreateShareDefaultRatioFixed,
        tour?.tourName,
        user?.email,
        flags?.welcomeMatTitle,
      ]
    );
    const [showShareTourModal, { setFalse: setFalseShowShareTourModal }] =
      useBoolean(true);
    const [
      showCopyShareLinkModal,
      {
        setTrue: setTrueCopyShareLinkModal,
        setFalse: setFalseCopyShareLinkModal,
      },
    ] = useBoolean(false);
    const [shareLink, setShareLink] = useState<ShareLink | null>(null);
    const [createTourShare, { isLoading: isLoadingCreateShare }] =
      useCreateShareMutation();
    const handleCreateShareLink = ({
      linkDescription,
      skipWelcome,
      cover,
      expiresAt,
      daysToExpire,
      security,
      tourAspectRatio,
      autoSyncUpdates,
      salesforceLeads,
      hubspotLeads,
      slackIntegration,
      welcomeMatTitle,
    }: ShareTourModalState) => {
      createTourShare({
        workspaceId,
        organizationId: String(organizationId),
        sourceType: ShareSourceType.tour,
        id: tourId,
        linkDescription,
        skipWelcome,
        cover: flags?.welcomeMatConfig ? cover : undefined,
        expiresAt,
        daysToExpire,
        security,
        tourAspectRatio,
        autoSyncUpdates,
        integrationLeads: Object.keys(
          removeUndefinedValues({
            salesforce: salesforceLeads,
            hubspot: hubspotLeads,
            slack: slackIntegration,
          })
        ) as IntegrationVendor[],
        welcomeMatTitle,
      })
        .unwrap()
        .then((share) => {
          setTrueCopyShareLinkModal();
          notify.success('Link was created successfully');
          setShareLink(share);
          onShareSuccess?.(share);
        })
        .catch(() => {
          modal.remove();
          notify.error('Sorry, something went wrong');
        })
        .finally(setFalseShowShareTourModal);
    };

    return (
      <>
        {tour && user && showShareTourModal && (
          <ShareTourModal
            organizationId={organizationId}
            toursShareAspectRatioEnabled={toursShareAspectRatioEnabled}
            title="Share tour"
            initialValues={initialShareTourModalValues}
            handleSubmit={(values) => {
              trackEvent(AnalyticsTrackEventName.TOUR_CLICK_SHARE_APPROVE, {
                location,
                shareData: values,
              });
              handleCreateShareLink(values);
            }}
            open={showShareTourModal}
            onClose={() => {
              trackEvent(AnalyticsTrackEventName.TOUR_CLICK_SHARE_CANCEL, {
                location,
              });
              modal.remove();
              setFalseShowShareTourModal();
            }}
            isLoading={isLoadingCreateShare}
          />
        )}
        {shareLink && (
          <CopyShareLinkModal
            open={showCopyShareLinkModal}
            onClose={() => {
              modal.remove();
              setFalseCopyShareLinkModal();
            }}
            shareLink={shareLink}
            goToShares={goToShares}
            tour
            subText={
              <TextBase
                label="Copy the link below and share your tour"
                color="text-ds-natural-400"
              />
            }
          />
        )}
      </>
    );
  }
);
